import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { defaultData, loadData as loadLocal, saveData as saveLocal, clearData as clearLocal } from '../storage.js'
import {
  loadData,
  loadTransactionsByMonth,
  loadTransactionsByRange,
  saveSettings, saveCategories,
  createTransaction, patchTransaction, deleteTransaction, replaceAllTransactions,
} from '../api.js'
import { todayLil } from '../lil.jsx'
import { pushRecentCurrency } from '../recentCurrencies.js'
import { displayDate, isSplitTx } from '../format.js'
import {
  idbGetAllTx, idbPutTx, idbDeleteTx, idbReplaceAllTx,
  idbGetKV, idbSetKV, idbClearAll,
  outboxAll, outboxDelete, outboxCount,
  enqueueCreate, enqueuePatch, enqueueDelete, enqueueSettings, enqueueCategories,
  SETTINGS_KEY, CATEGORIES_KEY,
} from '../idb.js'

function todayYearMonth() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const MIGRATION_FLAG = 'gunsi_tally_migrated_db_v1'
const SAVE_DEBOUNCE_MS = 500

function pickSettings(d) {
  return {
    currency:    d?.currency    ?? 'EUR',
    calendar:    d?.calendar    ?? 'gregorian',
    yearEra:     d?.yearEra     ?? 'lil',
    dayCalendar: d?.dayCalendar ?? 'lil',
    dateFormat:  d?.dateFormat  ?? 'auto',
    ledgerLinkEnabled:  Boolean(d?.ledgerLinkEnabled),
    ledgerCategoryId:   d?.ledgerCategoryId   ?? null,
    ledgerNameTemplate: d?.ledgerNameTemplate ?? 'Quota {periodLabel}',
  }
}

function sameSettings(a, b) {
  if (!a || !b) return false
  return a.currency === b.currency
    && a.calendar === b.calendar
    && a.yearEra === b.yearEra
    && a.dayCalendar === b.dayCalendar
    && a.dateFormat === b.dateFormat
    && a.ledgerLinkEnabled === b.ledgerLinkEnabled
    && a.ledgerCategoryId === b.ledgerCategoryId
    && a.ledgerNameTemplate === b.ledgerNameTemplate
}

// ── Helpers ───────────────────────────────────────────────────────────────
export function yearMonthOf(isoDate) {
  const d = new Date(isoDate)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// Derive [from, to) Greg-day range from a "YYYY-MM" cache key.
function gregRangeFromKey(key) {
  const m = /^(\d{4})-(\d{2})$/.exec(key)
  if (!m) return null
  const y = Number(m[1]), mo = Number(m[2])
  const pad = (n) => String(n).padStart(2, '0')
  const ny = mo === 12 ? y + 1 : y
  const nm = mo === 12 ? 1 : mo + 1
  return { from: `${y}-${pad(mo)}-01`, to: `${ny}-${pad(nm)}-01` }
}

// Half-open day-range check. isoDate may be a full ISO datetime — we compare
// on the YYYY-MM-DD prefix (UTC), matching how the backend filters.
function dateInRange(isoDate, from, to) {
  if (!from || !to || !isoDate) return false
  const d = String(isoDate).slice(0, 10)
  return d >= from && d < to
}

function sortDesc(list) {
  list.sort((a, b) =>
    new Date(displayDate(b)).getTime() - new Date(displayDate(a)).getTime()
  )
  return list
}

const TallyContext = createContext(null)

export function TallyProvider({ children }) {
  // Settings + categories live in `data`. Transactions are NOT stored here;
  // they're fetched per-month into `txCache` on demand.
  const [data, setData] = useState(null) // null = loading
  const [error, setError] = useState(null)
  // Map<cacheKey, { txs, from, to }>. Cached so navigating between months is
  // instant after the first fetch. `from`/`to` are the half-open Greg-day
  // range covered by that entry — used by add/update/remove to decide which
  // cached entries to patch optimistically. Cleared on resetAll.
  const [txCache, setTxCache] = useState(() => new Map())
  // Shared "currently viewed period" so Stats and Transactions stay in sync
  // as the user navigates between them.
  const [viewedGreg, setViewedGreg] = useState(todayYearMonth)
  const [viewedLil,  setViewedLil]  = useState(todayLil)
  // Set<yearMonth> currently fetching (avoid duplicate in-flight requests)
  const [loadingMonths, setLoadingMonths] = useState(() => new Set())

  // Offline/sync state (for the status badge)
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine))
  const [pendingCount, setPendingCount] = useState(0)

  // Refs for debounced saves + sync
  const dataRef         = useRef(null)
  const initialized     = useRef(false)
  const settingsTimer   = useRef(null)
  const categoriesTimer = useRef(null)
  const prevSettings    = useRef(null)
  const prevCategories  = useRef(null)
  const txCacheRef      = useRef(txCache)
  const flushRef        = useRef(null)
  const flushingRef     = useRef(false)

  useEffect(() => { dataRef.current = data }, [data])
  useEffect(() => { txCacheRef.current = txCache }, [txCache])

  // ── Offline mirror + mutation outbox ──────────────────────────────────────
  const refreshPending = useCallback(async () => {
    try { setPendingCount(await outboxCount()) } catch { /* ignore */ }
  }, [])

  // Swap an optimistic tx with the server-enriched one (carries amount_base).
  const applyServerTx = useCallback((serverTx) => {
    if (!serverTx?.id) return
    setTxCache(prev => {
      let changed = false
      const next = new Map(prev)
      for (const [k, entry] of prev) {
        const idx = entry.txs.findIndex(t => t.id === serverTx.id)
        if (idx < 0) continue
        const arr = [...entry.txs]; arr[idx] = serverTx
        next.set(k, { ...entry, txs: arr }); changed = true
      }
      return changed ? next : prev
    })
    idbPutTx(serverTx).catch(() => {})
  }, [])

  // Replay queued mutations to the backend, in order. Stops on the first
  // network failure (still offline) and retries on the next 'online' event.
  const flush = useCallback(async () => {
    if (flushingRef.current) return
    if (typeof navigator !== 'undefined' && !navigator.onLine) return
    flushingRef.current = true
    try {
      const records = await outboxAll()
      for (const rec of records) {
        try {
          if (rec.action === 'upsert') {
            let server = null
            try { server = await createTransaction(rec.tx) }
            catch (err) {
              // Already on the server (dup id) → update it instead.
              if (String(err?.message).includes('→ 409')) server = await patchTransaction(rec.tx.id, rec.tx)
              else throw err
            }
            if (server) applyServerTx(server)
          } else if (rec.action === 'patch') {
            try {
              const server = await patchTransaction(rec.key, rec.patch)
              if (server) applyServerTx(server)
            } catch (err) {
              if (!String(err?.message).includes('→ 404')) throw err // gone → drop
            }
          } else if (rec.action === 'delete') {
            try { await deleteTransaction(rec.key) }
            catch (err) { if (!String(err?.message).includes('→ 404')) throw err }
          } else if (rec.action === 'settings') {
            await saveSettings(rec.settings)
          } else if (rec.action === 'categories') {
            await saveCategories(rec.categories)
          }
          await outboxDelete(rec.key)
        } catch (err) {
          console.warn('[tally] flush interrupted, will retry when online', err)
          break
        }
      }
    } finally {
      flushingRef.current = false
      refreshPending()
    }
  }, [applyServerTx, refreshPending])
  useEffect(() => { flushRef.current = flush }, [flush])

  // Online/offline listeners → flush the outbox as soon as we reconnect.
  useEffect(() => {
    refreshPending()
    const onOnline  = () => { setOnline(true); flush() }
    const onOffline = () => setOnline(false)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [flush, refreshPending])

  // ── Initial load (settings + categories only) ──────────────────────────────
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const remote = await loadData()
        if (!alive) return

        // One-shot migration: backend empty + localStorage has data → push up
        const local = loadLocal()
        const hasLocalData = (local.categories?.length ?? 0) > 0 || (local.transactions?.length ?? 0) > 0
        const backendEmpty = (remote.categories?.length ?? 0) === 0
        const alreadyMigrated = localStorage.getItem(MIGRATION_FLAG) === '1'

        // Legacy localStorage values for the display prefs (per-device, pre-DB era)
        const lsCal  = (() => { try { return localStorage.getItem('gunsi_tally_calendar') } catch { return null } })()
        const lsEra  = (() => { try { return localStorage.getItem('gunsi_tally_year_era') } catch { return null } })()
        const lsDCal = (() => { try { return localStorage.getItem('gunsi_tally_day_calendar') } catch { return null } })()

        const settingsFrom = (src) => ({
          currency:    src.currency    ?? 'EUR',
          calendar:    src.calendar    ?? (lsCal  === 'lil'       ? 'lil'       : 'gregorian'),
          yearEra:     src.yearEra     ?? (lsEra  === 'he'        ? 'he'        : 'lil'),
          dayCalendar: src.dayCalendar ?? (lsDCal === 'gregorian' ? 'gregorian' : 'lil'),
          dateFormat:  src.dateFormat  ?? 'auto',
          ledgerLinkEnabled:  Boolean(src.ledgerLinkEnabled),
          ledgerCategoryId:   src.ledgerCategoryId   ?? null,
          ledgerNameTemplate: src.ledgerNameTemplate ?? 'Quota {periodLabel}',
        })

        let merged
        if (hasLocalData && backendEmpty && !alreadyMigrated) {
          const s = settingsFrom(local)
          await Promise.all([
            saveSettings(s),
            saveCategories(local.categories ?? []),
            replaceAllTransactions(local.transactions ?? []),
          ])
          localStorage.setItem(MIGRATION_FLAG, '1')
          merged = { ...s, categories: local.categories ?? [] }
        } else {
          merged = { ...settingsFrom(remote), categories: remote.categories ?? [] }
          if (!backendEmpty) localStorage.setItem(MIGRATION_FLAG, '1')
          // If backend was missing any of the calendar prefs but we had LS values,
          // push them now so they sync going forward.
          const remoteMissingPrefs =
            remote.calendar === null || remote.yearEra === null || remote.dayCalendar === null
          if (remoteMissingPrefs && (lsCal || lsEra || lsDCal)) {
            saveSettings({
              calendar:    merged.calendar,
              yearEra:     merged.yearEra,
              dayCalendar: merged.dayCalendar,
            }).catch(console.error)
          }
        }

        setData(merged)
        prevSettings.current   = pickSettings(merged)
        prevCategories.current = merged.categories
        idbSetKV(SETTINGS_KEY, pickSettings(merged)).catch(() => {})
        idbSetKV(CATEGORIES_KEY, merged.categories).catch(() => {})

        // Push any queued offline edits, then refresh the full local mirror so
        // every past month is available offline.
        ;(async () => {
          await flushRef.current?.()
          if (typeof navigator !== 'undefined' && !navigator.onLine) return
          // Don't overwrite the mirror while edits are still queued — a full
          // sync from the server would drop the not-yet-sent ones.
          try { if ((await outboxCount()) > 0) return } catch { /* ignore */ }
          try {
            const all = await loadTransactionsByRange('1900-01-01', '9999-12-31')
            await idbReplaceAllTx(all ?? [])
          } catch { /* stay on whatever the mirror already has */ }
        })()
      } catch (e) {
        if (!alive) return
        console.warn('[tally] online load failed — using local mirror', e)
        setError(e)
        // Offline boot: read the last-known settings/categories from IndexedDB
        // (falling back to the localStorage mirror).
        let savedSettings = null, savedCats = null
        try { savedSettings = await idbGetKV(SETTINGS_KEY) } catch { /* ignore */ }
        try { savedCats     = await idbGetKV(CATEGORIES_KEY) } catch { /* ignore */ }
        if (!alive) return
        const local = loadLocal()
        const s = savedSettings ?? pickSettings({ currency: local.currency })
        const cats = savedCats ?? local.categories ?? []
        setData({ ...s, categories: cats })
        prevSettings.current   = pickSettings(s)
        prevCategories.current = cats
      }
    })()
    return () => { alive = false }
  }, [])

  useEffect(() => {
    if (data !== null && !initialized.current) initialized.current = true
  }, [data])

  // ── Auto-save (settings, categories) — debounced batch ────────────────────
  // One timer batches ALL setting changes (currency + calendar + yearEra + dayCalendar)
  useEffect(() => {
    if (!initialized.current || !data) return
    const next = pickSettings(data)
    if (sameSettings(next, prevSettings.current)) return
    prevSettings.current = next
    clearTimeout(settingsTimer.current)
    settingsTimer.current = setTimeout(() => {
      enqueueSettings(pickSettings(dataRef.current))
        .then(() => { refreshPending(); flush() })
        .catch(console.error)
    }, SAVE_DEBOUNCE_MS)
  }, [data?.currency, data?.calendar, data?.yearEra, data?.dayCalendar, data?.dateFormat,
      data?.ledgerLinkEnabled, data?.ledgerCategoryId, data?.ledgerNameTemplate,
      flush, refreshPending])

  useEffect(() => {
    if (!initialized.current || !data) return
    if (data.categories === prevCategories.current) return
    prevCategories.current = data.categories
    clearTimeout(categoriesTimer.current)
    categoriesTimer.current = setTimeout(() => {
      enqueueCategories(dataRef.current.categories)
        .then(() => { refreshPending(); flush() })
        .catch(console.error)
    }, SAVE_DEBOUNCE_MS)
  }, [data?.categories, flush, refreshPending])

  // Offline mirror (settings + categories) — localStorage + IndexedDB snapshot
  useEffect(() => {
    if (!initialized.current || !data) return
    saveLocal({ ...loadLocal(), currency: data.currency, categories: data.categories })
    idbSetKV(SETTINGS_KEY, pickSettings(data)).catch(() => {})
    idbSetKV(CATEGORIES_KEY, data.categories).catch(() => {})
  }, [data])

  // ── Settings ─────────────────────────────────────────────
  const setCurrency    = useCallback((currency)    => setData(d => ({ ...d, currency })), [])
  const setCalendar    = useCallback((calendar)    => setData(d => ({ ...d, calendar })), [])
  const setYearEra     = useCallback((yearEra)     => setData(d => ({ ...d, yearEra })), [])
  const setDayCalendar = useCallback((dayCalendar) => setData(d => ({ ...d, dayCalendar })), [])
  const setDateFormat  = useCallback((dateFormat)  => setData(d => ({ ...d, dateFormat })), [])
  const setLedgerLinkEnabled  = useCallback((v) => setData(d => ({ ...d, ledgerLinkEnabled: !!v })), [])
  const setLedgerCategoryId   = useCallback((v) => setData(d => ({ ...d, ledgerCategoryId: v || null })), [])
  const setLedgerNameTemplate = useCallback((v) => setData(d => ({ ...d, ledgerNameTemplate: String(v ?? '') })), [])

  // ── Categories ───────────────────────────────────────────
  const addCategory = useCallback((category) => {
    const id = crypto.randomUUID()
    const type = category.type ?? 'expense'
    const icon = category.icon ?? (type === 'income' ? '💰' : '💸')
    setData(d => ({
      ...d,
      categories: [...d.categories, { id, type, icon, ...category }],
    }))
    return id
  }, [])

  const updateCategory = useCallback((id, patch) => {
    setData(d => ({
      ...d,
      categories: d.categories.map(c => c.id === id ? { ...c, ...patch } : c),
    }))
  }, [])

  const removeCategory = useCallback((id) => {
    setData(d => {
      const removed = d.categories.find(c => c.id === id)
      const newParent = removed?.parentId
      return {
        ...d,
        categories: d.categories
          .filter(c => c.id !== id)
          .map(c => c.parentId === id ? { ...c, parentId: newParent } : c),
      }
    })
    // Nullify categoryId on transactions across all cached months + backend
    setTxCache(prev => {
      const next = new Map()
      for (const [k, entry] of prev) {
        next.set(k, {
          ...entry,
          txs: entry.txs.map(t => t.categoryId === id ? { ...t, categoryId: undefined } : t),
        })
      }
      return next
    })
    // Nullify categoryId on every affected tx — across ALL history via the
    // local mirror (not just loaded months) — updating the mirror + queueing
    // patches for the backend.
    idbGetAllTx()
      .then(all => {
        const affected = all.filter(t => t.categoryId === id)
        if (!affected.length) return
        idbPutTx(affected.map(t => ({ ...t, categoryId: null }))).catch(() => {})
        return Promise.all(affected.map(t => enqueuePatch(t.id, { categoryId: null })))
      })
      .then(() => { refreshPending(); flush() })
      .catch(console.error)
  }, [flush, refreshPending])

  // ── Transactions: cache + CRUD ────────────────────────────
  // Generic fetcher: any unique cacheKey + a function returning Promise<tx[]>.
  // `range` ({from, to}, half-open Greg days) lets add/update/remove know
  // which cached entries cover a given tx date. For Greg-mode keys (YYYY-MM)
  // we derive it from the key itself if not supplied.
  const fetchMonth = useCallback(async (cacheKey, fetcher, range) => {
    if (!cacheKey) return
    if (txCache.has(cacheKey)) return
    if (loadingMonths.has(cacheKey)) return
    const effRange = range ?? gregRangeFromKey(cacheKey)
    setLoadingMonths(prev => { const n = new Set(prev); n.add(cacheKey); return n })
    try {
      const txs = fetcher
        ? await fetcher()
        : await loadTransactionsByMonth(cacheKey)
      setTxCache(prev => new Map(prev).set(cacheKey, {
        txs: txs ?? [],
        from: effRange?.from ?? null,
        to:   effRange?.to   ?? null,
      }))
      idbPutTx(txs ?? []).catch(() => {}) // keep the local mirror fresh
    } catch (e) {
      // Offline (or server error): serve this month from the local mirror.
      try {
        const all = await idbGetAllTx()
        const from = effRange?.from ?? null
        const to   = effRange?.to   ?? null
        const inRange = (from && to)
          ? all.filter(tx =>
              isSplitTx(tx) // split parents may touch any month → keep, page filters slices
              || dateInRange(displayDate(tx), from, to)
              || dateInRange(tx.date, from, to))
          : all
        setTxCache(prev => new Map(prev).set(cacheKey, { txs: inRange, from, to }))
      } catch (e2) {
        console.error('[tally] fetchMonth offline fallback failed', cacheKey, e2)
      }
    } finally {
      setLoadingMonths(prev => { const n = new Set(prev); n.delete(cacheKey); return n })
    }
  }, [txCache, loadingMonths])

  const addTransaction = useCallback((tx) => {
    const id = crypto.randomUUID()
    // Naive wall-clock default (no TZ) so times never shift across timezones.
    const n = new Date()
    const p = (x) => String(x).padStart(2, '0')
    const date = tx.date ?? `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}T${p(n.getHours())}:${p(n.getMinutes())}`
    const newTx = { id, date, ...tx }
    if (tx.currency) pushRecentCurrency(tx.currency)
    // Insert into every cached entry whose range covers this tx's display
    // date (= effectiveDate ?? date).
    const dispDate = displayDate(newTx)
    setTxCache(prev => {
      let changed = false
      const next = new Map(prev)
      for (const [k, entry] of prev) {
        if (!dateInRange(dispDate, entry.from, entry.to)) continue
        next.set(k, { ...entry, txs: sortDesc([newTx, ...entry.txs]) })
        changed = true
      }
      return changed ? next : prev
    })
    // Write to the local mirror + queue for the backend. flush() sends it now
    // when online; when offline it stays queued until reconnection.
    idbPutTx(newTx).catch(() => {})
    enqueueCreate(newTx)
      .then(() => { refreshPending(); flush() })
      .catch(e => console.error('[tally] addTransaction enqueue failed', e))
    return id
  }, [flush, refreshPending])

  const updateTransaction = useCallback((id, patch) => {
    if (patch?.currency) pushRecentCurrency(patch.currency)
    setTxCache(prev => {
      let changed = false
      const next = new Map(prev)
      for (const [k, entry] of prev) {
        const found = entry.txs.find(t => t.id === id)
        const updated = found ? { ...found, ...patch } : null
        // Use displayDate (effectiveDate ?? date) — must mirror the backend
        // month-filter logic or the cache and the server disagree.
        const stillIn = updated ? dateInRange(displayDate(updated), entry.from, entry.to) : false
        const candidateDate = patch.effectiveDate ?? patch.date
        const willEnter = !found && candidateDate != null && dateInRange(candidateDate, entry.from, entry.to)
        if (found && stillIn) {
          next.set(k, { ...entry, txs: sortDesc(entry.txs.map(t => t.id === id ? updated : t)) })
          changed = true
        } else if (found && !stillIn) {
          next.set(k, { ...entry, txs: entry.txs.filter(t => t.id !== id) })
          changed = true
        } else if (willEnter) {
          // Need the full tx to insert. Skip — refetch will pick it up.
          // (Optimistic insert here would require knowing the un-patched tx,
          // which we don't have. The patch path tolerates this gap.)
        }
      }
      return changed ? next : prev
    })
    // Update the local mirror (merge patch onto the current doc) + queue the
    // patch. flush() applies the server-enriched tx (amount_base) when online.
    let existing = null
    for (const entry of txCacheRef.current.values()) {
      const f = entry.txs.find(t => t.id === id)
      if (f) { existing = f; break }
    }
    if (existing) idbPutTx({ ...existing, ...patch }).catch(() => {})
    enqueuePatch(id, patch)
      .then(() => { refreshPending(); flush() })
      .catch(e => console.error('[tally] updateTransaction enqueue failed', e))
  }, [flush, refreshPending])

  const removeTransaction = useCallback((id) => {
    setTxCache(prev => {
      let changed = false
      const next = new Map(prev)
      for (const [k, entry] of prev) {
        if (!entry.txs.some(t => t.id === id)) continue
        next.set(k, { ...entry, txs: entry.txs.filter(t => t.id !== id) })
        changed = true
      }
      return changed ? next : prev
    })
    idbDeleteTx(id).catch(() => {})
    enqueueDelete(id)
      .then(() => { refreshPending(); flush() })
      .catch(e => console.error('[tally] removeTransaction enqueue failed', e))
  }, [flush, refreshPending])

  // ── Bulk ─────────────────────────────────────────────────
  const replaceAll = useCallback(async (nextData) => {
    const next = { ...defaultData(), ...nextData }
    const settings = pickSettings(next)
    setData({ ...settings, categories: next.categories })
    setTxCache(new Map()) // invalidate all months
    // Refresh the local mirror (import replaces everything).
    idbReplaceAllTx(next.transactions ?? []).catch(() => {})
    idbSetKV(SETTINGS_KEY, settings).catch(() => {})
    idbSetKV(CATEGORIES_KEY, next.categories ?? []).catch(() => {})
    try {
      await Promise.all([
        saveSettings(settings),
        saveCategories(next.categories ?? []),
        replaceAllTransactions(next.transactions ?? []),
      ])
      refreshPending()
    } catch (e) {
      console.error('[tally] replaceAll backend sync failed', e)
    }
  }, [refreshPending])

  const resetAll = useCallback(async () => {
    clearLocal()
    localStorage.removeItem(MIGRATION_FLAG)
    const defaults = pickSettings(null)
    setData({ ...defaults, categories: [] })
    setTxCache(new Map())
    idbClearAll().catch(() => {})
    setPendingCount(0)
    try {
      await Promise.all([
        saveSettings(defaults),
        saveCategories([]),
        replaceAllTransactions([]),
      ])
    } catch (e) {
      console.error('[tally] resetAll backend sync failed', e)
    }
  }, [])

  // Drop every cached month — used after bulk operations on the backend
  // (e.g. Manage page renaming names) so subsequent navigations refetch.
  const clearTxCache = useCallback(() => { setTxCache(new Map()) }, [])

  // Ricarica completa on-demand (pull-to-refresh): svuota le pending in coda,
  // rilegge settings+categorie dal backend e invalida la cache dei mesi, così
  // le viste rifetchano i movimenti aggiornati.
  const refreshAll = useCallback(async () => {
    try { await flush() } catch {}
    try {
      const remote = await loadData()
      const merged = { ...settingsFrom(remote), categories: remote.categories ?? [] }
      setData(merged)
      prevSettings.current = pickSettings(merged)
      prevCategories.current = merged.categories
    } catch (e) {
      console.warn('[tally] refreshAll', e)
    }
    setTxCache(new Map())
  }, [flush])

  const loading = data === null
  const value = {
    data: data ?? { currency: 'EUR', calendar: 'gregorian', yearEra: 'lil', dayCalendar: 'lil', dateFormat: 'auto', categories: [] },
    loading,
    error,
    txCache,
    loadingMonths,
    fetchMonth,
    setCurrency, setCalendar, setYearEra, setDayCalendar, setDateFormat,
    setLedgerLinkEnabled, setLedgerCategoryId, setLedgerNameTemplate,
    addCategory, updateCategory, removeCategory,
    addTransaction, updateTransaction, removeTransaction,
    replaceAll, resetAll,
    clearTxCache, refreshAll,
    viewedGreg, setViewedGreg,
    viewedLil,  setViewedLil,
    online, pendingCount, syncNow: flush,
  }

  if (loading) {
    return (
      <TallyContext.Provider value={value}>
        <div className="loading-screen"><div className="loading-spinner" /></div>
      </TallyContext.Provider>
    )
  }

  return <TallyContext.Provider value={value}>{children}</TallyContext.Provider>
}

export function useTally() {
  const ctx = useContext(TallyContext)
  if (!ctx) throw new Error('useTally must be used inside <TallyProvider>')
  return ctx
}

// Convenience hook: fetches the requested month (if not already cached) and
// returns its transactions + loading flag.
export function useMonthTransactions(yearMonth) {
  const { txCache, loadingMonths, fetchMonth } = useTally()
  useEffect(() => { fetchMonth(yearMonth) }, [yearMonth, fetchMonth])
  return {
    transactions: txCache.get(yearMonth)?.txs ?? [],
    loading: loadingMonths.has(yearMonth) && !txCache.has(yearMonth),
  }
}

// Range-based variant for non-Gregorian month navigation (e.g. Lil calendar).
// `cacheKey` must be a stable string unique to (from, to).
export function useRangeTransactions(cacheKey, from, to) {
  const { txCache, loadingMonths, fetchMonth } = useTally()
  useEffect(() => {
    if (!cacheKey) return
    fetchMonth(cacheKey, () => loadTransactionsByRange(from, to), { from, to })
  }, [cacheKey, from, to, fetchMonth])
  return {
    transactions: txCache.get(cacheKey)?.txs ?? [],
    loading: loadingMonths.has(cacheKey) && !txCache.has(cacheKey),
  }
}
